/** The run queue: priority, queue order, and queued runs projected onto runner slots. @since 1.0.0 */
import { Option, Schema } from "effect";

/** 1 Urgent, 2 High, 3 Medium, 4 Low, 0 none (the default). @since 1.0.0 */
export const RunPriority = Schema.Literal(0, 1, 2, 3, 4);
export type RunPriority = typeof RunPriority.Type;

/** @since 1.0.0 */
export const PRIORITY_LABELS: Record<RunPriority, string> = {
  0: "No priority",
  1: "Urgent",
  2: "High",
  3: "Medium",
  4: "Low",
};

/** 0 (none) sorts after 4. @since 1.0.0 */
export const priorityOrder = (p: number): number => p === 0 ? 5 : p;

/** A `RunSummary` fits; an absent priority is 0. @since 1.0.0 */
export interface QueueEntry {
  readonly id: string;
  readonly createdAt: string;
  readonly priority?: number | undefined;
  readonly queueRank?: number | null | undefined;
}

/** The manual rank, else the creation time in ms. @since 1.0.0 */
export const positionOf = (run: Pick<QueueEntry, "queueRank" | "createdAt">): number =>
  run.queueRank ?? Date.parse(run.createdAt);

/** Priority, then position, then creation, then id. @since 1.0.0 */
export const queueCompare = (a: QueueEntry, b: QueueEntry): number =>
  priorityOrder(a.priority ?? 0) - priorityOrder(b.priority ?? 0) ||
  positionOf(a) - positionOf(b) ||
  Date.parse(a.createdAt) - Date.parse(b.createdAt) ||
  (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * A rank between the positions of the same-priority runs just before and just after: the
 * midpoint, or 1000 past the one there is, or 0. @since 1.0.0
 */
export const rankBetween = (before: Option.Option<number>, after: Option.Option<number>): number =>
  Option.match(before, {
    onNone: () => Option.match(after, { onNone: () => 0, onSome: (next) => next - 1000 }),
    onSome: (prev) => Option.match(after, { onNone: () => prev + 1000, onSome: (next) => (prev + next) / 2 }),
  });

/** Minutes from now; `queued` is in queue order. @since 1.0.0 */
export interface ScheduleInput {
  readonly slots: number;
  readonly running: ReadonlyArray<{ readonly id: string; readonly leftP50Min: number; readonly leftP90Min: number }>;
  readonly queued: ReadonlyArray<{ readonly id: string; readonly estP50Min: number; readonly estP90Min: number }>;
}

/** `endP90Min` comes from the p90 pass, the rest from the p50 pass. @since 1.0.0 */
export interface ProjectedRun {
  readonly slot: number;
  readonly startMin: number;
  readonly endMin: number;
  readonly endP90Min: number;
}

/** `byId` holds the running runs too. Lengths below 0 count as 0. @since 1.0.0 */
export interface Projection {
  readonly byId: Record<string, ProjectedRun>;
  readonly allDoneMin: number;
  readonly allDoneP90Min: number;
}

type Timed = { readonly id: string; readonly min: number };
type Placed = Omit<ProjectedRun, "endP90Min"> & { readonly id: string };
type Slots = { readonly free: ReadonlyArray<number>; readonly placed: ReadonlyArray<Placed> };

/** The slot that frees first; on a tie, the lowest. */
const firstFree = (free: ReadonlyArray<number>): number =>
  free.reduce((best, at, slot) => at < free[best] ? slot : best, 0);

/** Never fewer slots than running runs; with no slot, nothing queued is placed. */
const pass = (slots: number, running: ReadonlyArray<Timed>, queued: ReadonlyArray<Timed>): Slots => {
  const busy = running.map(({ id, min }, slot): Placed => ({ id, slot, startMin: 0, endMin: Math.max(0, min) }));
  const idle = Array.from({ length: Math.max(0, slots - running.length) }, () => 0);
  const start: Slots = { free: [...busy.map((run) => run.endMin), ...idle], placed: busy };
  return (start.free.length === 0 ? [] : queued).reduce((acc: Slots, { id, min }): Slots => {
    const slot = firstFree(acc.free);
    const startMin = acc.free[slot];
    const endMin = startMin + Math.max(0, min);
    return {
      free: acc.free.map((at, i) => i === slot ? endMin : at),
      placed: [...acc.placed, { id, slot, startMin, endMin }],
    };
  }, start);
};

/**
 * Greedy, on p50 lengths then again on p90: running run `i` (in the order given) holds slot
 * `i`, and each queued run, in order, takes the slot that frees first. @since 1.0.0
 */
export const projectSchedule = (input: ScheduleInput): Projection => {
  const run = (p90: boolean): Slots =>
    pass(
      input.slots,
      input.running.map((r) => ({ id: r.id, min: p90 ? r.leftP90Min : r.leftP50Min })),
      input.queued.map((q) => ({ id: q.id, min: p90 ? q.estP90Min : q.estP50Min })),
    );
  const [p50, p90] = [run(false), run(true)];
  return {
    byId: Object.fromEntries(p50.placed.map(({ id, ...at }, i) => [id, { ...at, endP90Min: p90.placed[i].endMin }])),
    allDoneMin: Math.max(0, ...p50.free),
    allDoneP90Min: Math.max(0, ...p90.free),
  };
};
